import { supabase } from '@/lib/supabase';

export type AdOfferKey = 'STARTER' | 'BUSINESS' | 'PREMIUM';

export interface AdOffer {
  key: AdOfferKey;
  name: string;
  price: number;
  tagline: string;
  features: string[];
  recommended: boolean;
  icon: string;
}

export const AD_OFFERS: AdOffer[] = [
  {
    key: 'STARTER',
    name: 'Starter',
    price: 30000,
    tagline: 'Pour lancer votre visibilité',
    icon: 'ri-rocket-2-line',
    features: [
      'Bannière publicitaire',
      'Lien direct vers votre entreprise',
      'Présence pendant 30 jours',
    ],
    recommended: false,
  },
  {
    key: 'BUSINESS',
    name: 'Business',
    price: 50000,
    tagline: 'Pour accélérer votre croissance',
    icon: 'ri-bar-chart-2-line',
    features: [
      'Bannière publicitaire sur plusieurs pages',
      'Lien direct vers votre entreprise',
      'Entreprise mise en avant',
      'Présence pendant 30 jours',
    ],
    recommended: false,
  },
  {
    key: 'PREMIUM',
    name: 'Premium',
    price: 100000,
    tagline: 'Visibilité maximale sur la plateforme',
    icon: 'ri-vip-crown-2-line',
    features: [
      "Grande bannière publicitaire sur la page d'accueil",
      "Mise en avant de l'entreprise",
      'Visibilité prioritaire',
      'Présence pendant 30 jours',
    ],
    recommended: true,
  },
];

/** Emplacement par défaut de la bannière selon l'offre choisie. */
export const OFFER_PLACEMENT: Record<AdOfferKey, string> = {
  STARTER: 'Page Média',
  BUSINESS: 'Plusieurs pages',
  PREMIUM: "Page d'accueil",
};

export type CampaignStatus = 'pending' | 'approved' | 'active' | 'completed' | 'rejected';

export const CAMPAIGN_STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  approved: 'Approuvée',
  active: 'Active',
  completed: 'Terminée',
  rejected: 'Refusée',
};

export interface AdCampaign {
  id: string;
  company_name: string;
  manager_name: string;
  phone: string;
  email: string;
  sector: string | null;
  website: string | null;
  company_page_url: string | null;
  offer: string;
  banner_url: string | null;
  start_date: string | null;
  duration_days: number | null;
  end_date: string | null;
  placement: string | null;
  status: string;
  payment_status: string;
  paydunya_token: string | null;
  paydunya_mode: string | null;
  created_at: string;
}

export interface SubmitCampaignInput {
  companyName: string;
  managerName: string;
  phone: string;
  email: string;
  sector: string;
  website: string;
  companyPageUrl: string;
  offer: AdOfferKey;
  bannerUrl: string | null;
  startDate: string;
  durationDays: number;
  placement: string;
}

const CAMPAIGN_BUCKET = 'media';

export async function uploadCampaignBanner(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
  const path = `ads/campaigns/${safeName}`;

  const { error } = await supabase.storage.from(CAMPAIGN_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;

  const { data } = supabase.storage.from(CAMPAIGN_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

function computeEndDate(startDate: string, durationDays: number): string | null {
  if (!startDate || !durationDays) return null;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + durationDays);
  return d.toISOString().slice(0, 10);
}

export async function submitAdCampaign(input: SubmitCampaignInput): Promise<AdCampaign> {
  const endDate = computeEndDate(input.startDate, input.durationDays);

  const { data, error } = await supabase
    .from('ad_campaigns')
    .insert({
      company_name: input.companyName.trim(),
      manager_name: input.managerName.trim(),
      phone: input.phone.trim(),
      email: input.email.trim(),
      sector: input.sector.trim() || null,
      website: input.website.trim() || null,
      company_page_url: input.companyPageUrl.trim() || null,
      offer: input.offer,
      banner_url: input.bannerUrl,
      start_date: input.startDate || null,
      duration_days: input.durationDays || null,
      end_date: endDate,
      placement: input.placement,
      status: 'pending',
    })
    .select()
    .single();
  if (error) throw error;
  return data as AdCampaign;
}

export async function fetchAdCampaigns(): Promise<AdCampaign[]> {
  const { data, error } = await supabase
    .from('ad_campaigns')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as AdCampaign[]) || [];
}

export async function updateAdCampaignStatus(id: string, status: CampaignStatus): Promise<void> {
  const { error } = await supabase.from('ad_campaigns').update({ status }).eq('id', id);
  if (error) throw error;
}

export async function deleteAdCampaign(id: string): Promise<void> {
  const { error } = await supabase.from('ad_campaigns').delete().eq('id', id);
  if (error) throw error;
}

export function calculateOfferAmount(offer: AdOffer, durationDays: number): number {
  const months = durationDays / 30;
  return Math.round(offer.price * months);
}

export async function initiatePaydunyaCheckout(
  campaignId: string,
  offer: AdOffer,
  durationDays: number,
  companyName: string,
  email: string,
  phone: string,
  cancelUrl: string,
  returnUrl: string,
): Promise<{ checkoutUrl: string; token: string }> {
  const amount = calculateOfferAmount(offer, durationDays);

  const { data, error } = await supabase.functions.invoke('paydunya-ads', {
    body: {
      action: 'checkout',
      campaignId,
      offer: offer.key,
      amount,
      companyName,
      email,
      phone,
      cancelUrl,
      returnUrl,
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  if (!data?.checkoutUrl || !data?.token) {
    throw new Error('Aucun lien de paiement reçu de PayDunya');
  }

  return { checkoutUrl: data.checkoutUrl, token: data.token };
}

export async function confirmPaydunyaPayment(token: string): Promise<{
  success: boolean;
  status: string;
  receiptUrl: string | null;
}> {
  const { data, error } = await supabase.functions.invoke('paydunya-ads', {
    body: { action: 'confirm', token },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);

  return {
    success: data?.success || false,
    status: data?.status || 'unknown',
    receiptUrl: data?.receiptUrl || null,
  };
}

export interface ProfileLinkResult {
  type: 'business' | 'freelancer' | null;
  url: string | null;
}

/**
 * Retrouve le lien public du compte connecté :
 * - une entreprise (page /business/:slug) s'il en possède une,
 * - sinon un profil freelance (page /freelancers/:ownerId).
 */
export async function fetchCurrentUserProfileLink(userId: string): Promise<ProfileLinkResult> {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const prefix = basePath ? `/${basePath}` : '';
  const origin = window.location.origin;

  const { data: business } = await supabase
    .from('businesses')
    .select('slug')
    .eq('owner_id', userId)
    .maybeSingle();

  if (business?.slug) {
    return { type: 'business', url: `${origin}${prefix}/business/${business.slug}` };
  }

  const { data: freelancer } = await supabase
    .from('freelancer_profiles')
    .select('owner_id')
    .eq('owner_id', userId)
    .maybeSingle();

  if (freelancer?.owner_id) {
    return { type: 'freelancer', url: `${origin}${prefix}/freelancers/${freelancer.owner_id}` };
  }

  return { type: null, url: null };
}